import { useQuery } from '@tanstack/react-query';
import type { MouseEvent } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { CenterFlex, Flex } from '@/components/base';
import GridTopList from '@/components/GridList/GridTopList';
import { MainText } from '@/components/Typography/NamedTypo';
import queryKeys from '@/configs/react-query-keys';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { useToast } from '@/stores/pure/ToastStore';
import useTagStore from '@/stores/TagStore';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { GPT_DOMAIN } from '@/types/gptConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';
import { getTagPath } from '@/utils/tag';

import SearchContentStyles from '../SearchContentStyles';
import SearchDrilldownMenu from '../SearchDrilldownMenu';

type SeletectedTag = {
  alias: string;
  name: string;
} | null;

const { ContentBox, ContentHeader, DownloadButton } = SearchContentStyles;

const TagTopListContainer = () => {
  const { toDetailPage, refreshHistoryList } = useGlobalSearchModalAction();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const addToast = useToast();
  const { searchResult, alias } = useSearchDetailContext();
  const tagMap = useTagStore((state) => state.tagMap);
  const { status, open, close, pos, modalState } =
    useModalState<SeletectedTag>(null);

  const { data, isLoading } = useQuery({
    queryFn: () =>
      service.globalSearch.fetchFeedbackFieldDist(
        searchResult.alias,
        FEEDBACK_COLUMNS.TAG,
      ),
    queryKey: [
      queryKeys.search.SEARCH_DETAIL,
      queryKeys.search.SEARCH_RESULT_TAGS,
      searchResult.alias,
    ],
  });

  const onViewFeedbackListHandler = async (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    close();
    if (!modalState) return;

    const filterPayload = {
      [FEEDBACK_COLUMNS.TAG]: structuredFilterUtils.buildSingleValue(
        modalState.alias,
      ),
    };

    setNewModal<FeedbackBlockListModalOptions>({
      alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      idx: 0,
      keys: ['tag', modalState.alias],
      options: {
        queryKeys: [modalState.alias],
        title: 'Feedback list',
        subTitle: `Tag: ${modalState.name}`,
        domain: 'global-search',
        fetcher: async (
          sortStatus: NotNullableSortStatusT,
          pageParam?: null | string,
        ) => {
          const res = await service.globalSearch.fetchSearchFeedbackList(
            alias,
            {
              query: {
                cursor: pageParam,
                sortby: sortStatus.sortBy,
                order: sortStatus.orderStatus,
              },
              payload: filterPayload,
            },
          );

          const PAGE_SIZE = 50;

          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token: res.feedbacks[PAGE_SIZE]?.alias,
          };
        },
        exportFetcher: async (reason?: string, password?: string) => {
          await service.globalSearch.exportSearchFeedbackList({
            alias: searchResult.alias,
            filterPayload: filterPayload,
            reason: reason,
            password: password,
          });
        },
        socketInfo: {
          domain: GPT_DOMAIN.GLOBAL_SEARCH,
          search: {
            alias: searchResult.alias,
            filterPayload: filterPayload,
          },
          initialState: initialOpen ? CollapseState.EXPANDED : undefined,
        },
      },
    });
  };

  const onAddToFilterHandler = async (e: MouseEvent) => {
    e.stopPropagation();
    close();

    if (!modalState) return;

    const hasTagFilter = searchResult.filter?.[FEEDBACK_COLUMNS.TAG];

    const newFilter = new SFilter(searchResult.filter);

    if (hasTagFilter) {
      if (!Array.isArray(hasTagFilter))
        throw new Error('Tag filter should be array');
      if (
        hasTagFilter.length > 1 &&
        hasTagFilter.find((f) => f.op === STRUCTURED_FILTER_OPERATOR.OR)
      ) {
        // 이미 taxonomy 필터를 갖고 있고 OR 연산자인 경우 필터 추가 불가능 안내
        addToast('Tag filter can not be multiple', 'fail');
        return;
      }
    }

    newFilter.addValueSet(FEEDBACK_COLUMNS.TAG, {
      op: STRUCTURED_FILTER_OPERATOR.OR,
      values: [modalState.alias],
    });

    const res = await service.globalSearch.submitSearch({
      title: `${searchResult.title} copy`,
      filter: newFilter.get(),
      query: searchResult.query,
      options: {
        ...searchResult.options,
      },
    });

    toDetailPage(res.alias);
    refreshHistoryList();
  };

  const onClickHandler = (
    e: MouseEvent,
    bucket: { alias: string; name: string },
  ) => {
    open({
      modalState: bucket,
      pos: {
        top: e.clientY,
        left: e.clientX,
      },
    });
  };

  return (
    <ContentBox padding="24px" g={24}>
      <Flex j="space-between" a="center">
        <ContentHeader>Related tags</ContentHeader>
        <DownloadButton onClick={() => {}} />
      </Flex>
      <ErrorBoundary
        fallback={
          <CenterFlex style={{ height: 250 }}>
            <MainText dim>Error occurred</MainText>
          </CenterFlex>
        }
      >
        <GridTopList
          data={data?.buckets.map((bucket) => {
            const paths = getTagPath(tagMap, bucket.bucket.toString()).map(
              (tag) => tag.name,
            );
            return {
              bucket: bucket.bucket,
              name: paths.join(' / '),
              count: bucket.count,
            };
          })}
          isLoading={isLoading}
          onClickItem={(e, bucket) =>
            onClickHandler(e, {
              alias: bucket.bucket.toString(),
              name: bucket.name || '',
            })
          }
        />
      </ErrorBoundary>
      <SearchDrilldownMenu
        pos={{
          top: pos?.top as number,
          left: pos?.left as number,
        }}
        status={status}
        close={close}
        onViewFeedbackListClick={onViewFeedbackListHandler}
        onAddToFilterClick={onAddToFilterHandler}
        onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
      />
    </ContentBox>
  );
};

export default TagTopListContainer;
